import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Image as ImageIcon, ArrowLeft, FileText, ClipboardList, Files, Link2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/admin/Sidebar';
import Header from '../../components/admin/Header';
import api from '../../utils/api';

export default function NewArtwork() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', sku: '', designId: '', packagingFormat: '', size: '', brand: '', sharePointLink: '' });
  const [users, setUsers] = useState([]);
  const [files, setFiles] = useState([]); // Changed to array for multiple files
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    // Add new files to existing list (don't replace)
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  }, []);

  useEffect(() => {
    api.get('/users')
      .then(res => setUsers(res.data.filter(u => u.role === 'marketing' && u.isActive)))
      .catch(() => {});
  }, []);

  const selectedBrandMarketers = users.filter(u => u.brand === form.brand);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [], 'application/pdf': [] },
    maxSize: 20 * 1024 * 1024,
    multiple: true  // Changed to true to allow multiple files
  });

  const removeFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const getFileMeta = (file) => {
    if (file.type === 'application/pdf') {
      return { icon: <FileText size={20} color="#ef4444" strokeWidth={2} />, bg: 'var(--danger-light)', label: 'PDF' };
    }
    return { icon: <ImageIcon size={20} color="#3b82f6" strokeWidth={2} />, bg: '#eff6ff', label: 'Image' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error('Please enter artwork title');
    if (!form.brand) return toast.error('Please select the artwork brand');
    if (files.length === 0) return toast.error('Please upload at least one file');

    setUploading(true);
    let uploadedImage = null;
    let uploadedDocument = null;

    try {
      // Upload all files and collect results
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
const uploadRes = await api.post('/upload', fd);

        // Separate image and document uploads
        if (uploadRes.data.isImage) {
          uploadedImage = {
            artworkUrl: uploadRes.data.url,
            artworkPublicId: uploadRes.data.publicId
          };
        } else {
          uploadedDocument = {
            artworkFileName: uploadRes.data.fileName,
            artworkMimeType: uploadRes.data.mimeType,
            artworkData: uploadRes.data.data
          };
        }
      }

      // At least one file should have been uploaded
      if (!uploadedImage && !uploadedDocument) {
        throw new Error('No files were successfully uploaded');
      }
    } catch (err) {
      toast.error('File upload failed: ' + (err.response?.data?.message || err.message));
      setUploading(false);
      return;
    }
    setUploading(false);
    setSubmitting(true);
    try {
      const uploadData = {};
      if (uploadedImage) Object.assign(uploadData, uploadedImage);
      if (uploadedDocument) Object.assign(uploadData, uploadedDocument);

      const res = await api.post('/artworks', { ...form, ...uploadData });
      toast.success('Artwork uploaded and sent for R&D review!');
      navigate(`/artworks/${res.data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = uploading || submitting;

  return (
    <div className="page-layout" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <style>{`
        .naf-dropzone { transition: all 0.2s; }
        .naf-dropzone:hover, .naf-dropzone.active { border-color: var(--brand-red) !important; background: var(--brand-red-light) !important; }
        .naf-dropzone:hover .naf-dropzone-icon, .naf-dropzone.active .naf-dropzone-icon { background: var(--white); color: var(--brand-red); }
        .naf-file-card { transition: border-color 0.15s, box-shadow 0.15s; }
        .naf-file-card:hover { border-color: var(--gray-300); box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
        .naf-remove-btn { transition: all 0.15s; }
        .naf-remove-btn:hover { background: var(--danger-light); color: var(--danger); }
        .naf-add-more { transition: all 0.2s; }
        .naf-add-more:hover, .naf-add-more.active { border-color: var(--brand-red) !important; background: var(--brand-red-light) !important; color: var(--brand-red) !important; }
      `}</style>

      <Sidebar />
      <div className="main-content">
        <Header title="Upload New Artwork" subtitle="Start a new packaging artwork approval workflow" />
        <div className="page-body">
          <div style={{ maxWidth: 1800, margin: '0 auto' }}>
            <button className="btn btn-ghost btn-sm" style={{ marginBottom: 20 }} onClick={() => navigate(-1)}>
              <ArrowLeft size={16} /> Back
            </button>

            <form onSubmit={handleSubmit}>
              <div className="card" style={{ padding: 28, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10, background: 'var(--brand-red-light)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-red)', flexShrink: 0
                  }}>
                    <ClipboardList size={18} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Artwork Details</h3>
                    <p style={{ fontSize: '0.8125rem', color: 'var(--gray-400)', margin: '2px 0 0' }}>
                      Basic information about this packaging artwork
                    </p>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Artwork Title *</label>
                    <input className="form-input" placeholder="e.g. Hollandia Yoghurt 500ml Redesign"
                      value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">SKU</label>
                    <input className="form-input" placeholder="e.g. HY-500ML-2024"
                      value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Brand *</label>
                    <select className="form-input"
                      value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} required>
                      <option value="">Select brand</option>
                      <option value="Hollandia">Hollandia</option>
                      <option value="Chivita">Chivita</option>
                    </select>
                    {form.brand && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 6 }}>
                        Marketing flow: {selectedBrandMarketers.length
                          ? selectedBrandMarketers.map(u => u.name).join(', ')
                          : `No active ${form.brand} marketer found`}
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Design ID</label>
                    <input className="form-input" placeholder="e.g. DES-20241201"
                      value={form.designId} onChange={e => setForm({ ...form, designId: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Packaging Format</label>
                    <select className="form-input"
                      value={form.packagingFormat} onChange={e => setForm({ ...form, packagingFormat: e.target.value })}>
                      <option value="">Select packaging format</option>
                      <option value="TBA">TBA</option>
                      <option value="TGA">TGA</option>
                      <option value="TCA">TCA</option>
                      <option value="TFA">TFA</option>
                      <option value="TPA">TPA</option>
                      <option value="COMBI Sleeve">COMBI Sleeve</option>
                      <option value="Pouch">Pouch</option>
                      <option value="Can">Can</option>
                      <option value="Wrapper">Wrapper</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Size</label>
                    <textarea className="form-input" placeholder="e.g. 500ml, 1L, A4, etc." rows="3"
                      value={form.size} onChange={e => setForm({ ...form, size: e.target.value })} style={{ resize: 'vertical', fontFamily: 'inherit' }} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Link2 size={13} /> SharePoint Link (Optional)
                    </label>
                    <input className="form-input" placeholder="e.g. https://chilimited-my.sharepoint.com/personal/..."
                      value={form.sharePointLink} onChange={e => setForm({ ...form, sharePointLink: e.target.value })} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 6 }}>
                      Paste the SharePoint link to the artwork folder. Only assigned vendors will be able to access it.
                    </p>
                  </div>
                </div>
              </div>

              <div className="card" style={{ padding: 28, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 10, background: 'var(--info-light)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--info)', flexShrink: 0
                    }}>
                      <Files size={18} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Stage 1 Artwork Files</h3>
                      <p style={{ fontSize: '0.8125rem', color: 'var(--gray-400)', margin: '2px 0 0' }}>
                        For Pre-NAFDAC approval review
                      </p>
                    </div>
                  </div>
                  {files.length > 0 && (
                    <span className="badge badge-gray">{files.length} file{files.length > 1 ? 's' : ''} selected</span>
                  )}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: 20 }}>
                  You can upload both image and PDF files. Accepted formats: PNG, JPG, PDF (max 20MB each)
                </p>

                {files.length === 0 ? (
                  <div {...getRootProps()} className={`naf-dropzone ${isDragActive ? 'active' : ''}`} style={{
                    border: '2px dashed var(--gray-200)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '48px 24px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: 'var(--gray-50)'
                  }}>
                    <input {...getInputProps()} />
                    <div className="naf-dropzone-icon" style={{
                      width: 60, height: 60, borderRadius: '50%', background: 'var(--gray-100)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 14px', color: 'var(--gray-400)', transition: 'all 0.2s'
                    }}>
                      <Upload size={26} />
                    </div>
                    <p style={{ fontWeight: 600, color: 'var(--gray-700)', marginBottom: 4 }}>
                      {isDragActive ? 'Drop to upload' : 'Drag & drop files here'}
                    </p>
                    <p style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>
                      or click to browse · you can select multiple files
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                    {files.map((file, idx) => {
                      const meta = getFileMeta(file);
                      return (
                        <div key={idx} className="naf-file-card" style={{
                          border: '1.5px solid var(--gray-200)',
                          borderRadius: 'var(--radius-lg)',
                          padding: 16,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 16
                        }}>
                          <div style={{
                            width: 48, height: 48, background: meta.bg, borderRadius: 'var(--radius-md)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                          }}>
                            {meta.icon}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{
                              fontWeight: 600, fontSize: '0.875rem', margin: 0,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}>
                              {file.name}
                            </p>
                            <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 2 }}>
                              {meta.label} · {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                          <button
                            type="button"
                            className="naf-remove-btn"
                            onClick={() => removeFile(idx)}
                            aria-label="Remove file"
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              width: 34, height: 34, borderRadius: 'var(--radius-sm)',
                              border: 'none', background: 'transparent', color: 'var(--gray-400)',
                              cursor: 'pointer', flexShrink: 0
                            }}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      );
                    })}

                    <div {...getRootProps()} className={`naf-add-more ${isDragActive ? 'active' : ''}`} style={{
                      border: '2px dashed var(--gray-200)',
                      borderRadius: 'var(--radius-lg)',
                      padding: '16px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      background: 'var(--gray-50)',
                      color: 'var(--gray-600)'
                    }}>
                      <input {...getInputProps()} />
                      <Plus size={15} />
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                        {isDragActive ? 'Drop to add more' : 'Add more files'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
                  {uploading ? <><span className="spinner" /> Uploading…</> :
                   submitting ? <><span className="spinner" /> Submitting…</> :
                   <><Upload size={16} /> Submit for Approval</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
